<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Checkbox from 'primevue/checkbox'
import BBtn from '@lib/components/base/BBtn.vue'
import { sdk } from '@/graphql/client'
import { useToast } from '@lib/composables/useToast'
import type { User, InstanceRights } from '@/composables/useUsersData'

const props = defineProps<{ user: User }>()

const { t } = useI18n()
const toast = useToast()

// Editovatelná pole (mimo primary, které je read-only sync flag).
type Editable = {
    bzucoAdmin: boolean
    allowManageUsers: boolean
    allowManageEvents: boolean
    allowManageTicketsets: boolean
    allowManageTickets: boolean
    allowManageDevices: boolean
    allowStats: boolean
}

type Row = InstanceRights & { local: Editable; saving: boolean }

const rows = ref<Row[]>([])

function toLocal(ir: InstanceRights): Editable {
    return {
        bzucoAdmin: ir.bzucoAdmin,
        allowManageUsers: ir.allowManageUsers,
        allowManageEvents: ir.allowManageEvents,
        allowManageTicketsets: ir.allowManageTicketsets,
        allowManageTickets: ir.allowManageTickets,
        allowManageDevices: ir.allowManageDevices,
        allowStats: ir.allowStats
    }
}

function rebuildRows() {
    rows.value = props.user.instances.map(ir => ({
        ...ir,
        local: toLocal(ir),
        saving: false
    }))
}

watch(() => props.user.id, rebuildRows, { immediate: true })
watch(() => props.user.instances, rebuildRows, { deep: true })

const FIELDS: Array<keyof Editable> = [
    'bzucoAdmin',
    'allowManageUsers',
    'allowManageEvents',
    'allowManageTicketsets',
    'allowManageTickets',
    'allowManageDevices',
    'allowStats'
]

function isRowDirty(row: Row): boolean {
    return FIELDS.some(k => row.local[k] !== (row as unknown as Record<string, boolean>)[k])
}

const anyDirty = computed(() => rows.value.some(isRowDirty))
defineExpose({ isDirty: () => anyDirty.value })

async function saveRow(row: Row) {
    if (!isRowDirty(row) || row.saving) return
    row.saving = true
    try {
        // Runtime IDs from backend are strings (Graphene default ID scalar),
        // but TypeScript thinks they're numbers (interface mismatch).
        // Backend mutation requires Int — convert explicitly.
        const userId = Number(props.user.id)
        const instanceId = parseInt(String(row.instance.id), 10)
        if (Number.isNaN(userId) || Number.isNaN(instanceId)) {
            toast.error(t('common.errors.generic'))
            return
        }
        const r = await sdk.AdminUpdateUserRights({
            userId,
            instanceId,
            ...row.local
        })
        if (r.admin?.user?.updateRights?.ok) {
            // Shift saved baseline = local become new saved. Panel stays
            // open so admin can edit multiple instances without re-clicking
            // the row each time.
            for (const k of FIELDS) {
                ;(row as unknown as Record<string, boolean>)[k] = row.local[k]
            }
            toast.success(t('users.toast.rightsSaved'))
        } else {
            toast.error(t('common.errors.generic'))
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        row.saving = false
    }
}
</script>

<template>
    <div v-if="rows.length === 0" class="text-xs text-gray-400 py-2">
        {{ t('users.rights.empty') }}
    </div>
    <div v-else class="overflow-x-auto">
        <table class="w-full text-xs">
            <thead class="bg-gray-50 text-gray-600">
                <tr>
                    <th class="px-3 py-2 text-left font-semibold">{{ t('users.rights.cols.instance') }}</th>
                    <th class="px-3 py-2 text-center font-semibold">{{ t('users.rights.cols.bzucoAdmin') }}</th>
                    <th class="px-3 py-2 text-center font-semibold">{{ t('users.rights.cols.manageUsers') }}</th>
                    <th class="px-3 py-2 text-center font-semibold">{{ t('users.rights.cols.manageEvents') }}</th>
                    <th class="px-3 py-2 text-center font-semibold">{{ t('users.rights.cols.manageTicketsets') }}</th>
                    <th class="px-3 py-2 text-center font-semibold">{{ t('users.rights.cols.manageTickets') }}</th>
                    <th class="px-3 py-2 text-center font-semibold">{{ t('users.rights.cols.manageDevices') }}</th>
                    <th class="px-3 py-2 text-center font-semibold">{{ t('users.rights.cols.stats') }}</th>
                    <th class="px-3 py-2 text-center font-semibold">{{ t('users.rights.cols.primary') }}</th>
                    <th class="px-3 py-2" style="width: 90px"></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="row in rows"
                    :key="row.id"
                    :class="[
                        'border-t border-gray-200',
                        isRowDirty(row) ? 'bg-orange-l' : ''
                    ]"
                    :title="isRowDirty(row) ? t('users.rights.dirtyIndicator') : ''"
                >
                    <td class="px-3 py-1.5 font-medium text-ink relative">
                        <span
                            class="absolute left-0 top-0 bottom-0 w-1 transition-colors"
                            :class="isRowDirty(row) ? 'bg-orange' : 'bg-transparent'"
                        />
                        {{ row.instance.name }}
                    </td>
                    <td v-for="f in FIELDS" :key="f" class="px-3 py-1.5 text-center">
                        <Checkbox
                            :binary="true"
                            :model-value="row.local[f]"
                            @update:model-value="(v: boolean) => (row.local[f] = v)"
                        />
                    </td>
                    <td class="px-3 py-1.5 text-center" :title="t('users.bzucoSynced.primaryTooltip')">
                        <span v-if="row.primary" class="text-teal">●</span>
                        <span v-else class="text-gray-300">○</span>
                    </td>
                    <td class="px-3 py-1.5 text-right">
                        <BBtn
                            size="small"
                            :disabled="row.saving"
                            :class="isRowDirty(row) ? 'visible' : 'invisible'"
                            type="button"
                            @click="saveRow(row)"
                        >
                            {{ t('users.rights.savePerRow') }}
                        </BBtn>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
